import {
	allgemeinerVergleich, BATCH_EDIT_HISTORY_LIMIT_PER_POINT, GanzerWert, KonfliktVergleich,
	statusBadge,
} from '@luzula/luzula-lib';
import { BOHRPUNKT_BATCH_EDIT_FIELDS } from './batch-edit/bohrpunkt-batch-edit-fields';

/**
 * Was bpweb über seine Punkte weiß, damit zwei Fassungen gegenübergestellt
 * werden können. Die Mechanik steht in der Bibliothek
 * (`allgemeinerVergleich`), hier steht nur die Fachkunde.
 *
 * ## Klartext statt Feldnamen
 *
 * Der allgemeine Vergleich zeigt die Feldnamen, wie sie im Datensatz stehen —
 * „nummer", „substratFeuchte". Das liest niemand gern. Die Klartexte gibt es
 * aber längst: Der Feldkatalog der Sammelbearbeitung beschreibt dieselben
 * Felder schon mit Beschriftung und Gruppe. Er wird hier einfach
 * weiterverwendet, statt dieselbe Liste ein zweites Mal zu pflegen.
 *
 * ## Zeilenlisten ganz oder gar nicht
 *
 * Horizontfolge, Substratfolge und Bodenvegetation sind Zeilenlisten, deren
 * Zeilen **keine eigene Kennung** tragen. Zeilenweise zusammenzuführen hieße
 * raten: Der Platz in der Liste kann in der anderen Fassung eine ganz andere
 * Zeile sein, und zwei getrennte Aufnahmen stehen beide auf Platz 1. Deshalb
 * zählt jede dieser Listen als EIN Unterschied und wird im Ganzen übernommen
 * (Entscheidung vom 29.08.2026).
 *
 * Angezeigt wird dabei die Zeilenzahl. Das ist wenig, aber ehrlich: Man sieht,
 * dass es die eine oder die andere Aufnahme wird, und nicht eine Mischung, die
 * so nie jemand erhoben hat.
 */

/* ------------------------------------------------------------------------- *
 * Was KEINE Frage an einen Menschen ist
 *
 * Nutzer-Befund 10.09.2026: Der Balken meldete 533 Datensaetze, und im
 * Klaer-Fenster stand keine einzige beantwortbare Frage - nur Zeitstempel des
 * Statusverlaufs und Haken an Pruefhinweisen. Solche Felder weichen bei zwei
 * Fassungen IMMER ab, ganz unabhaengig davon, ob jemand fachlich etwas anderes
 * eingetragen hat.
 *
 * Dieselbe Trennung hat bpweb laengst getroffen, nur an anderer Stelle:
 * `DataService.AUDIT_EXCLUDE` fuehrt `workflow`, `checkerComments`,
 * `batchEditHistory` und `state` als "keine fachliche Datenaenderung". Hier
 * steht sie noch einmal, fuer den Vergleich zweier Fassungen - mit einem
 * Unterschied: Der **Status** ist sehr wohl eine Frage.
 * ------------------------------------------------------------------------- */

/**
 * Felder, die gar nicht zur Sprache kommen.
 *
 * - `state` ist die abgeleitete Zusammenfassung der Pruefhinweise
 *   (`commentsSeverity`, `hasDiskussions`, `resolved`) und wird aus ihnen
 *   berechnet - daran gibt es nichts zu entscheiden.
 * - `batchEditHistory` ist ein Protokoll der Sammelbearbeitungen. Es wird
 *   zusammengefuehrt statt zur Wahl gestellt (siehe unten).
 * - Die beiden **gemerkten Vorschlaege** sind reine Buchhaltung: Sie halten
 *   fest, welchen Wert die App zuletzt selbst vorgeschlagen hat, damit sie einen
 *   von Hand geaenderten nicht wieder ueberschreibt (`evaluators/entwaesserung.ts`,
 *   `evaluators/substratgruppe.ts`). Sie stehen in keiner Maske; gefragt wird
 *   nach dem Wert daneben, nicht nach dem Merkzettel. Weggelassen heisst: Der
 *   Merkzettel der eigenen Fassung bleibt stehen - und das ist der richtige, denn
 *   die eigene Fassung wird ja geschrieben.
 */
export const BOHRPUNKT_NICHT_ZEIGEN = [
	'state',
	'batchEditHistory',
	'extendedHeader.entwaesserungVorschlag',
	'extendedHeader.stfg.substratGruppeVorschlag',
];

/**
 * Felder, die **die App selbst nachtraegt** — kein Mensch tippt sie ein.
 *
 * Nutzer-Befund 11.09.2026: Zwoelf Punkte gemeldet, an denen niemand gearbeitet
 * hatte. Im Fenster stand in „Meine Fassung" ueberall ein Strich und in der
 * anderen die Forstadresse; im naechsten Fall dasselbe Bild mit der
 * Bestandesbeschreibung. Auf einem Geraet lief die Anreicherung (online), auf
 * dem anderen nicht (offline) — und schon standen sich eine leere und eine
 * volle Fassung gegenueber.
 *
 * Ist ein solches Feld auf genau EINER Seite gefuellt, gilt die gefuellte:
 * **Nichts gegen Etwas ist kein Streit.** Tragen beide Seiten etwas, bleibt es
 * eine ganz normale Frage. Die Mechanik steht in der Bibliothek
 * (`selbstNachgetragen`), hier steht nur, welche Felder es sind.
 *
 * Es ging nicht nur um Laerm: Die Vorauswahl steht auf der eigenen Fassung, und
 * „Entscheidung speichern" haette die leere festgeschrieben und die volle
 * geloescht.
 *
 * ## Woher die Liste kommt
 *
 * **Die Anreicherung an der Koordinate** (`BohrpunktWmsFieldMappingHandler`):
 * Sie fragt vier Dinge ab und traegt sie nach, wenn das jeweilige Leitfeld leer
 * ist — Klimastufe, Forstadresse, Katasteradresse, Wuchsgebiet. Angestossen
 * wird sie beim Laden eines Punktes (`DataService.updateBohrpunkt`), sie
 * speichert selbst.
 *
 * **Die Sammelkonvertierung der Bestandestexte** (`bestand-batch.ts`,
 * `applyBestand`): Sie baut aus dem eingegebenen Text die strukturierte
 * Beschreibung und legt den Originaltext daneben.
 *
 * ## Was bewusst NICHT dazugehoert
 *
 * `extendedHeader.baumarten` — den Bestandestext tippt ein Mensch. Die
 * Konvertierung schreibt ihn zwar in die genormte Form um, aber ob der rohe und
 * der genormte Text dasselbe meinen, liesse sich hier nur raten. Also wird
 * gefragt.
 *
 * `extendedHeader.wuchsbezirk` faehrt bei der Abfrage mit, steht aber nicht in
 * der Liste: Er wird zusammen mit dem Wuchsgebiet gesetzt und deckt sich damit.
 */
export const BOHRPUNKT_SELBST_NACHGETRAGEN = [
	// Anreicherung an der Koordinate
	'extendedHeader.klimaStufe',
	'header.forstamt',
	'header.revier',
	'header.adresse.abteilung',
	'header.adresse.unterabteilung',
	'header.adresse.teilflaeche',
	'header.adresse.gemarkung',
	'header.adresse.flur',
	'header.adresse.flurstueck',
	'extendedHeader.wuchsgebiet',
	'extendedHeader.wuchsbezirk',
	// Sammelkonvertierung der Bestandestexte. Ein Pfad genuegt: Schichten,
	// Gruppen, Arten und Wuchsklassen haengen alle unter `bestand`.
	'extendedHeader.bestand',
	'extendedHeader.bestandFreitext',
	// Der Gruppen-Stempel (feature/user-groups): `DataService.save` setzt ihn bei
	// jedem Speichern aus dem Projekt, kein Mensch tippt ihn. Punkte, die per
	// Uebertrag aus einer Datenbank ohne Gruppen kommen, stehen sonst mit einer
	// einzigen Zeile „groups 1: /SelfManaged/LFMV gegen –" vor einem Menschen
	// (Nutzer-Befund 25.09.2026, Torgelow) — und ohne Stempel waere der Punkt fuer
	// die Gruppe unsichtbar. Zwei VERSCHIEDENE Stempel bleiben eine Frage.
	'groups',
];

/**
 * Der Workflow-Block gilt als **eine** Frage, und die Frage ist der Status.
 *
 * Er gehoert zusammen: Status, Zeitpunkt des Wechsels, Bearbeiter-Verlauf. Das
 * Datenmodell sagt es selbst ("Komplettes Sub-Objekt erleichtert die
 * Konfliktaufloesung"). Feld fuer Feld entschieden entstuende ein Punkt, der
 * auf "Vorgelegt" steht, aber den Zeitpunkt und den Bearbeiter der anderen
 * Fassung traegt - ein Zustand, in dem er nie war.
 *
 * **Verglichen wird am Status**, nicht am Inhalt (`amTextVergleichen`): Sonst
 * stuende hier immer eine Frage, denn irgendein Zeitstempel weicht immer ab.
 * Fuehren beide Seiten denselben Status, gibt es nichts zu entscheiden.
 *
 * Der **Bearbeiter-Verlauf** (`editors`) faehrt bewusst im Block mit und wird
 * NICHT zusammengefuehrt: An seinem letzten Eintrag haengt, wem der Punkt
 * gehoert und wer ihn bearbeiten darf. Zwei Verlaeufe zu mischen hiesse, ueber
 * Berechtigungen zu entscheiden - und das gehoert nicht in eine
 * Konfliktaufloesung.
 */
function statusText(wf: any): string {
	const status = wf?.status;
	if (!status) return 'ohne Status';
	return statusBadge(status)?.label ?? String(status);
}

/** Der Workflow-Block, immer als Ganzes. */
export const BOHRPUNKT_WORKFLOW_ALS_GANZES: Record<string, GanzerWert> = {
	workflow: { feld: 'Status', zeige: statusText, amTextVergleichen: true },
};

/**
 * Zwei Protokolle vereinen: alles behalten, Doppeltes einmal, nach Zeit
 * geordnet.
 *
 * Ein Protokoll wird nur angehaengt - beide Fassungen sind also echte
 * Ausschnitte desselben Geschehens, und beide sind wahr. Es zur Wahl zu
 * stellen hiesse, einen Teil der Pruefspur wegzuwerfen; einen Menschen danach
 * zu fragen hiesse, ihn etwas entscheiden zu lassen, wovon nichts abhaengt
 * (Nutzer-Entscheidung 10.09.2026).
 *
 * Doppelt ist, was Zeichen fuer Zeichen gleich ist. Das ist streng, aber
 * ehrlich: Zwei Eintraege, die sich in irgendetwas unterscheiden, sind zwei
 * Vorgaenge.
 */
function vereineProtokoll(
	a: Record<string, any>[] | undefined,
	b: Record<string, any>[] | undefined,
	zeitfeld: string,
	hoechstens?: number,
): Record<string, any>[] {
	const gesehen = new Set<string>();
	const vereint = [...(a ?? []), ...(b ?? [])].filter(eintrag => {
		const kennzeichen = JSON.stringify(eintrag);
		if (gesehen.has(kennzeichen)) return false;
		gesehen.add(kennzeichen);
		return true;
	});
	vereint.sort((x, y) => (x?.[zeitfeld] ?? 0) - (y?.[zeitfeld] ?? 0));
	// Das Protokoll der Sammelbearbeitung hat eine Obergrenze je Punkt; die
	// aeltesten Eintraege fallen zuerst.
	return hoechstens && vereint.length > hoechstens ? vereint.slice(-hoechstens) : vereint;
}

/**
 * Haengt an das Ergebnis, was ohne Rueckfrage vereint wird.
 *
 * Laeuft **nach** der Uebernahme der gewaehlten Werte: Welcher Workflow-Block
 * auch gewinnt, der Verlauf beider Seiten steht danach darin.
 */
export function fuehreProtokolleZusammen(ergebnis: any, meins: any, fremd: any): any {
	if (!ergebnis || typeof ergebnis !== 'object') return ergebnis;

	const verlauf = vereineProtokoll(meins?.workflow?.history, fremd?.workflow?.history, 'time');
	// Ohne Workflow-Block gibt es nichts anzuhaengen - einen anzulegen waere
	// eine Behauptung ueber einen Punkt, der nie einen hatte.
	if (ergebnis.workflow && verlauf.length) {
		ergebnis.workflow = { ...ergebnis.workflow, history: verlauf };
	}

	const sammel = vereineProtokoll(
		meins?.batchEditHistory, fremd?.batchEditHistory, 'timestamp',
		BATCH_EDIT_HISTORY_LIMIT_PER_POINT,
	);
	if (sammel.length) ergebnis.batchEditHistory = sammel;

	return ergebnis;
}


/** „3 Zeilen" — mehr sagt eine Liste in der Gegenüberstellung nicht. */
function zeilenzahl(wert: any): string {
	if (!Array.isArray(wert)) return 'vorhanden';
	return wert.length === 1 ? '1 Zeile' : `${wert.length} Zeilen`;
}

/**
 * Die Zeilenlisten des Punktes, je Pfad ohne Listen-Stellen.
 * `bodenvegetation.aufnahme.vegetationen` ist die Liste der Pflanzenarten.
 */
export const BOHRPUNKT_GANZE_LISTEN: Record<string, GanzerWert> = {
	Horizontfolge: { feld: 'Horizontfolge', zeige: zeilenzahl },
	Substratfolge: { feld: 'Substratfolge', zeige: zeilenzahl },
	'bodenvegetation.aufnahme.vegetationen': { feld: 'Bodenvegetation', zeige: zeilenzahl },
};

/**
 * Klartext je Feldname aus dem Katalog der Sammelbearbeitung.
 *
 * Der Katalog führt die Felder als Pfad (`header.nummer`); der Vergleich fragt
 * nach dem letzten Stück (`nummer`). Felder mit eigenem Zugriff (Pfad mit
 * `[]`) bleiben außen vor: Sie stehen für ganze Zeilengruppen, nicht für einen
 * Wert. Tragen zwei Felder denselben letzten Namen, gewinnt das zuletzt
 * eingetragene — beide meinen dann ohnehin dasselbe Wort.
 */
export function bohrpunktBeschriftungen(): Record<string, string> {
	const namen: Record<string, string> = {};
	for (const feld of BOHRPUNKT_BATCH_EDIT_FIELDS) {
		if (!feld?.key || feld.key.includes('[]')) continue;
		const letztes = feld.key.split('.').pop();
		if (letztes && feld.label) namen[letztes] = feld.label;
	}
	// Die Zeilenlisten stehen nicht im Katalog der Sammelbearbeitung (dort sind
	// bewusst nur die einfachen Kopf-Felder), gehören hier aber benannt.
	namen['Horizontfolge'] = 'Horizontfolge';
	namen['Substratfolge'] = 'Substratfolge';
	namen['vegetationen'] = 'Bodenvegetation';
	// Die Pruefhinweise stehen nach FELDNAMEN abgelegt, nicht als Liste. Ohne
	// Klartext stand dort blankes "resolved" (Nutzer-Befund 10.09.2026); zu
	// welchem Feld der Hinweis gehoert, nennt jetzt der Bezug.
	namen['resolved'] = 'Hinweis erledigt';
	namen['comments'] = 'Hinweistexte';
	namen['checkerComments'] = 'Pruefhinweise';
	// Der Katalog ist nach dem LETZTEN Namensstueck abgelegt, und das ist nicht
	// eindeutig: `extendedHeader.klimaStufe.neu` und
	// `header.humusform.aufnahmeFlaeche.neu` enden beide auf „neu". Die
	// Klimastufe trug deshalb die Beschriftung „Humusform" (Nutzer-Bild
	// 11.09.2026). Ein Klartext am GANZEN Pfad geht dem am blossen Namen vor.
	// „Klimastufe" ist das Wort, das auch im Formular steht (boden.component.html).
	namen['extendedHeader.klimaStufe.neu'] = 'Klimastufe';
	namen['extendedHeader.klimaStufe'] = 'Klimastufe';
	return namen;
}

/** Der Weg zu einer Zeilenliste im Punkt-Dokument. */
const LISTEN_PFADE: Record<string, (doc: any) => any> = {
	Horizontfolge: doc => doc?.Horizontfolge,
	Substratfolge: doc => doc?.Substratfolge,
	'bodenvegetation.aufnahme.vegetationen': doc => doc?.bodenvegetation?.aufnahme?.vegetationen,
};

/** Tragen alle Zeilen dieser Liste in BEIDEN Fassungen eine Kennung? */
function alleZeilenGekennzeichnet(meins: any, fremd: any, hole: (doc: any) => any): boolean {
	const zeilen = [hole(meins), hole(fremd)];
	let gesehen = 0;
	for (const liste of zeilen) {
		if (liste === undefined || liste === null) continue;
		if (!Array.isArray(liste)) return false;
		for (const zeile of liste) {
			if (!zeile?.id) return false;
			gesehen++;
		}
	}
	// Zwei leere Listen gibt es nichts zu entscheiden; die Regel spielt dann
	// keine Rolle. Sie als „gekennzeichnet" zu behandeln waere trotzdem falsch,
	// denn ein Unterschied kaeme nur aus einer der beiden Seiten.
	return gesehen > 0;
}

/**
 * Die Listen, die für DIESE beiden Fassungen als Ganzes gelten müssen.
 *
 * Tragen alle Zeilen einer Liste auf beiden Seiten eine Kennung, findet der
 * allgemeine Vergleich dieselbe Zeile in beiden Fassungen wieder (er sucht nach
 * `id`, `uid`, `kennung`, `_id`) und man entscheidet **zeilenweise**. Dann darf
 * die Liste nicht mehr als ein Klumpen behandelt werden.
 *
 * Fehlt auch nur einer Zeile die Kennung, bleibt es beim Ganzen: Für diese
 * Zeile ließe sich nur über ihren Platz in der Liste zuordnen, und das wäre
 * geraten. Jede Liste wird für sich betrachtet.
 */
export function ganzeListenFuer(meins: any, fremd: any): Record<string, GanzerWert> {
	const ganz: Record<string, GanzerWert> = {};
	for (const [pfad, hole] of Object.entries(LISTEN_PFADE)) {
		if (alleZeilenGekennzeichnet(meins, fremd, hole)) continue;
		ganz[pfad] = BOHRPUNKT_GANZE_LISTEN[pfad]!;
	}
	return ganz;
}

/**
 * Der Feldvergleich für zwei Fassungen eines Punktes.
 *
 * Er wird je Fassungspaar frisch gebaut, weil erst an den beiden Fassungen
 * hängt, ob eine Liste zeilenweise oder im Ganzen entschieden wird. Wichtig ist
 * dabei, dass Vergleich und Übernahme **dieselbe** Regel verwenden: Sonst
 * stünde eine Zeile zur Wahl, die beim Schreiben nicht gefunden würde.
 */
export function bohrpunktKonfliktVergleich(): KonfliktVergleich {
	const beschriftungen = bohrpunktBeschriftungen();
	const bauen = (meins: any, fremd: any) => allgemeinerVergleich({
		beschriftungen,
		nichtZeigen: BOHRPUNKT_NICHT_ZEIGEN,
		selbstNachgetragen: BOHRPUNKT_SELBST_NACHGETRAGEN,
		alsGanzes: { ...BOHRPUNKT_WORKFLOW_ALS_GANZES, ...ganzeListenFuer(meins, fremd) },
	});

	return {
		unterschiede: (meins, fremd) => bauen(meins, fremd).unterschiede(meins, fremd),
		// Erst die gewaehlten Werte uebernehmen, DANN die Protokolle vereinen:
		// Welcher Workflow-Block auch gewinnt, der Verlauf beider Seiten steht
		// danach darin.
		wendeAn: (meins, fremd, schluessel) => fuehreProtokolleZusammen(
			bauen(meins, fremd).wendeAn(meins, fremd, schluessel), meins, fremd,
		),
		zusatz: (meins, fremd) => bauen(meins, fremd).zusatz?.(meins, fremd) ?? '',
		// Mit der gemeinsamen Ausgangsfassung gilt, was nur eine Seite geaendert
		// hat, ohne Rueckfrage (Lib, `ausgangsfassung.ts`). Ohne diese Zeile
		// arbeitete die Bibliothek fuer Punkte weiter ohne sie.
		dreiwegeRegeln: (meins, fremd) => bauen(meins, fremd).dreiwegeRegeln?.(meins, fremd) ?? {},
	};
}
